/**
 * Tiện ích xử lý tham số tìm kiếm bất động sản trên URL.
 *
 * Module này chuyển đổi giữa bộ lọc tìm kiếm (state UI) và query string,
 * nhận diện tìm kiếm đang active, ánh xạ preset giá, và chuẩn hóa dữ liệu
 * trước khi submit (ví dụ: tách tên tỉnh khỏi ô search).
 */
import {
  AMENITY_FILTER_FIELDS,
  PROPERTY_SORTS,
  ROOM_SPEC_FILTER_FIELDS,
  TRANSACTION_TYPES,
} from '../constants/property';
import { findMatchingProvince, normalizeLocationKey } from './searchMapLocation';

/** Tên param dùng cho danh mục trang chủ trên URL. */
export const HOME_CATEGORY_PARAM = 'home_category';

const ROOM_SPEC_PARAM_KEYS = ROOM_SPEC_FILTER_FIELDS.map(({ field }) => field);
const AMENITY_PARAM_KEYS = AMENITY_FILTER_FIELDS.map(({ field }) => field);

/** Các key query string được coi là bộ lọc tìm kiếm thực sự (không tính page). */
const PROPERTY_SEARCH_PARAM_KEYS = [
  'search',
  'province',
  'district',
  'ward',
  'transaction_type',
  'category_id',
  'min_price',
  'max_price',
  'min_area',
  'max_area',
  ...ROOM_SPEC_PARAM_KEYS,
  ...AMENITY_PARAM_KEYS,
  'sort',
];

const createEmptyRoomSpecFilters = () =>
  Object.fromEntries(ROOM_SPEC_PARAM_KEYS.map((key) => [key, '']));

const createEmptyAmenityFilters = () =>
  Object.fromEntries(AMENITY_PARAM_KEYS.map((key) => [key, '']));

/** Giá trị mặc định của toàn bộ bộ lọc tìm kiếm bất động sản. */
export const DEFAULT_PROPERTY_SEARCH_FILTERS = {
  search: '',
  province: '',
  district: '',
  ward: '',
  transaction_type: TRANSACTION_TYPES.sale,
  category_id: '',
  min_price: '',
  max_price: '',
  min_area: '',
  max_area: '',
  ...createEmptyRoomSpecFilters(),
  ...createEmptyAmenityFilters(),
  sort: PROPERTY_SORTS.latest,
  page: '1',
};

/**
 * Kiểm tra URL có đang chứa bộ lọc tìm kiếm khác mặc định hay không.
 * Param `section` (browse từ carousel trang chủ) luôn được coi là active.
 *
 * @param {URLSearchParams} searchParams - Query string hiện tại.
 * @returns {boolean}
 */
export const hasActivePropertySearch = (searchParams) => {
  if (searchParams.get('section')) {
    return true;
  }

  return PROPERTY_SEARCH_PARAM_KEYS.some((key) => {
    const value = searchParams.get(key);

    if (!value) {
      return false;
    }

    // Giá trị mặc định không được tính là đang lọc
    if (key === 'sort' && value === PROPERTY_SORTS.latest) {
      return false;
    }

    if (key === 'transaction_type' && value === TRANSACTION_TYPES.sale) {
      return false;
    }

    return true;
  });
};

/**
 * Đọc query string và trả về object bộ lọc tương ứng.
 * `transaction_type` luôn cố định là bán — giá trị trên URL bị bỏ qua.
 *
 * @param {URLSearchParams} searchParams
 * @returns {Object}
 */
export const parsePropertySearchParams = (searchParams) => ({
  search: searchParams.get('search') || '',
  province: searchParams.get('province') || '',
  district: searchParams.get('district') || '',
  ward: searchParams.get('ward') || '',
  transaction_type: TRANSACTION_TYPES.sale,
  category_id: searchParams.get('category_id') || '',
  min_price: searchParams.get('min_price') || '',
  max_price: searchParams.get('max_price') || '',
  min_area: searchParams.get('min_area') || '',
  max_area: searchParams.get('max_area') || '',
  ...Object.fromEntries(
    ROOM_SPEC_PARAM_KEYS.map((key) => [key, searchParams.get(key) || ''])
  ),
  ...Object.fromEntries(
    AMENITY_PARAM_KEYS.map((key) => [key, searchParams.get(key) || ''])
  ),
  section: searchParams.get('section') || '',
  source: searchParams.get('source') || '',
});

/**
 * Chuyển object bộ lọc thành chuỗi query (bỏ qua giá trị rỗng và mặc định).
 *
 * @param {Object} filters
 * @returns {string}
 */
export const buildPropertySearchQuery = (filters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    if (key === 'transaction_type' && value === TRANSACTION_TYPES.sale) {
      return;
    }

    params.set(key, value);
  });

  return params.toString();
};

/**
 * Suy ra id preset giá từ cặp min/max đang chọn.
 *
 * @param {string} minPrice
 * @param {string} maxPrice
 * @returns {string} Id preset hoặc chuỗi rỗng nếu không khớp.
 */
export const getPricePresetId = (minPrice, maxPrice) => {
  if (!minPrice && maxPrice === '3000000000') {
    return 'under-3b';
  }

  if (minPrice === '3000000000' && maxPrice === '7000000000') {
    return '3b-7b';
  }

  if (minPrice === '7000000000' && !maxPrice) {
    return 'over-7b';
  }

  if (minPrice === '1000000000' && !maxPrice) {
    return 'from-1b';
  }

  return '';
};

/**
 * Ánh xạ id preset giá sang cặp min_price / max_price.
 *
 * @param {string} presetId
 * @returns {{ min_price: string, max_price: string }}
 */
export const getPricePresetValues = (presetId) => {
  switch (presetId) {
    case 'under-3b':
      return { min_price: '', max_price: '3000000000' };
    case '3b-7b':
      return { min_price: '3000000000', max_price: '7000000000' };
    case 'over-7b':
      return { min_price: '7000000000', max_price: '' };
    case 'from-1b':
      return { min_price: '1000000000', max_price: '' };
    default:
      return { min_price: '', max_price: '' };
  }
};

/**
 * Tạo đường dẫn trang chủ kèm query tìm kiếm đã chuẩn hóa.
 *
 * @param {Object} filters
 * @returns {string}
 */
export const buildHomeSearchPath = (filters) => {
  const query = buildPropertySearchQuery(sanitizeSearchFiltersForSubmit(filters));
  return query ? `/?${query}` : '/';
};

/**
 * Chuẩn hóa bộ lọc trước khi gửi: nếu ô search khớp tên tỉnh thì chuyển sang filter province.
 *
 * @param {Object} filters
 * @returns {Object}
 */
export const sanitizeSearchFiltersForSubmit = (filters) => {
  const next = { ...filters };

  if (!next.search) {
    return next;
  }

  const matchedProvince = findMatchingProvince(next.search);

  if (matchedProvince) {
    next.province = matchedProvince;
    next.district = '';
    next.ward = '';
    next.search = '';

    return next;
  }

  // Tránh lặp: search trùng với province đã chọn thì xóa search
  if (next.province && normalizeLocationKey(next.search) === normalizeLocationKey(next.province)) {
    next.search = '';
  }

  return next;
};

/**
 * Xóa các bộ lọc nâng cao (giá, diện tích, phòng, tiện ích) nhưng giữ filter cơ bản.
 *
 * @param {Object} filters
 * @returns {Object}
 */
export const clearExtraSearchFilters = (filters) => ({
  ...filters,
  transaction_type: TRANSACTION_TYPES.sale,
  min_price: '',
  max_price: '',
  min_area: '',
  max_area: '',
  ...createEmptyRoomSpecFilters(),
  ...createEmptyAmenityFilters(),
});

/**
 * Đếm số bộ lọc nâng cao đang được áp dụng (dùng hiển thị badge).
 *
 * @param {Object} filters
 * @returns {number}
 */
export const countExtraSearchFilters = (filters) =>
  [
    filters.min_price || filters.max_price,
    filters.min_area || filters.max_area,
    ...ROOM_SPEC_PARAM_KEYS.map((key) => filters[key]),
    ...AMENITY_PARAM_KEYS.map((key) => filters[key]),
  ].filter(Boolean).length;
